<h1 class="page-title">{{ 'club.title' | t }}</h1>

@if (loading && !summary) {
  <p class="muted">{{ 'account.loading' | t }}</p>
} @else if (error && !summary) {
  <p class="error-text">{{error}}</p>
  <button type="button" class="btn-pill btn-pill-outline" (click)="load()">{{ 'account.retry' | t }}</button>
} @else if (summary) {

  @if (!summary.enabled) {
    <div class="card warning">{{ 'club.closed' | t }}</div>
  }

  <!-- Solde + niveau -->
  <section class="card hero">
    <div class="balances">
      <div class="balance main">
        <mat-icon class="coin">stars</mat-icon>
        <div>
          <strong class="balance-value">{{summary.available | number}}</strong>
          <span class="muted">{{ 'club.available' | t }}</span>
        </div>
      </div>
      <div class="balance">
        <strong>{{summary.pending | number}}</strong>
        <span class="muted">{{ 'club.pending' | t }}</span>
      </div>
      <div class="balance">
        <strong>{{summary.lifetime | number}}</strong>
        <span class="muted">{{ 'club.lifetime' | t }}</span>
      </div>
      <a class="btn-pill btn-pill-accent shop-btn" routerLink="/compte/rewards"><mat-icon>redeem</mat-icon>{{ 'club.goToShop' | t }}</a>
    </div>
    @if (summary.pending > 0) {
      <p class="muted hint"><mat-icon>schedule</mat-icon>{{ 'club.pendingHint' | t: { days: summary.rules.maturityDays } }}</p>
    }

    <div class="level-head">
      <strong [class]="'level-' + summary.level">{{ 'club.level' | t: { level: ('club.levels.' + summary.level | t) } }}</strong>
      <span class="muted">
        @if (summary.nextLevel) {
          {{ 'club.nextLevel' | t: { points: pointsToNextLevel, level: ('club.levels.' + summary.nextLevel | t) } }}
        } @else {
          {{ 'club.maxLevel' | t }}
        }
      </span>
    </div>
    <div class="level-track">
      <div class="progress"><span [style.width.%]="levelProgress"></span></div>
      <div class="level-steps">
        @for (level of summary.levels; track level.id) {
          <div class="level-step" [class.reached]="isReached(level)">
            <span class="dot"></span>
            <span [class]="'level-' + level.id">{{ 'club.levels.' + level.id | t }}</span>
            <small class="muted">{{level.threshold}}</small>
          </div>
        }
      </div>
    </div>
    <p class="muted">{{ 'club.levelBasis' | t: { points: summary.earned12m } }}</p>
  </section>

  <!-- Comment gagner des points -->
  <section class="card">
    <h2 class="card-title">{{ 'club.howToEarn' | t }} <small class="muted">{{ 'club.last12Months' | t }}</small></h2>
    <div class="grid-2">
      @for (rule of earnRules; track rule.reason) {
        <div class="earn-card">
          <div class="earn-head">
            <mat-icon>{{rule.icon}}</mat-icon>
            <strong>{{ 'club.earn.' + rule.reason | t }}</strong>
            <span class="earned">{{summary.breakdown[rule.reason] || 0}}</span>
          </div>
          <p class="muted">{{ 'club.earn.' + rule.reason + 'Hint' | t: rule.hintParams }}</p>
        </div>
      }

      <div class="earn-card">
        <div class="earn-head">
          <mat-icon>how_to_vote</mat-icon>
          <strong>{{ 'club.earn.weeklyVotes' | t }}</strong>
          <span class="earned">{{summary.breakdown['weeklyVotes'] || 0}}</span>
        </div>
        <p class="muted">{{ 'club.earn.weeklyVotesHint' | t: { points: summary.weekly.points, count: summary.weekly.required } }}</p>
        <div class="segments">
          @for (done of weekSegments; track $index) {
            <span [class.done]="done"></span>
          }
        </div>
        <small class="muted">{{ 'club.weekProgress' | t: { votes: summary.weekly.votes, required: summary.weekly.required, start: date(summary.weekly.weekStart, false), end: date(summary.weekly.weekEnd, false) } }}</small>
      </div>

      <div class="earn-card">
        <div class="earn-head">
          <mat-icon>bolt</mat-icon>
          <strong>{{ 'club.earn.voteStreak' | t }}</strong>
          <span class="earned">{{summary.breakdown['voteStreak'] || 0}}</span>
        </div>
        <p class="muted">
          {{ 'club.streak' | t: { weeks: summary.weekly.streak } }}
          @if (nextStreakBonus) {
            · {{ 'club.nextStreakBonus' | t: { points: nextStreakBonus[1], weeks: nextStreakBonus[0] } }}
          }
        </p>
        <div class="streak-track">
          <div class="progress"><span [style.width.%]="streakProgress"></span></div>
          @for (bonus of summary.weekly.streakBonuses; track bonus[0]) {
            <span class="streak-step" [style.inset-inline-start.%]="streakPosition(bonus[0])" [class.reached]="summary.weekly.streak >= bonus[0]">
              <span class="dot"></span>
              <small>{{ 'club.weeksShort' | t: { weeks: bonus[0] } }}</small>
              <small class="accent">+{{bonus[1]}}</small>
            </span>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Niveaux et avantages -->
  <section class="card">
    <h2 class="card-title">{{ 'club.levelsTitle' | t }}</h2>
    <div class="levels-grid">
      @for (level of summary.levels; track level.id) {
        <div class="level-card" [class.current]="level.id === summary.level" [class.locked]="!isReached(level)">
          <mat-icon class="level-icon" [class]="'level-icon level-' + level.id">{{ isReached(level) ? 'military_tech' : 'lock' }}</mat-icon>
          <strong>{{ 'club.levels.' + level.id | t }}</strong>
          <span class="chip">{{ 'club.levelFrom' | t: { points: level.threshold } }}</span>
          @if (level.id === summary.level) {
            <span class="chip approved">{{ 'club.currentLevel' | t }}</span>
          }
          <ul class="perks">
            <li><mat-icon>trending_up</mat-icon>{{ 'club.multiplier' | t: { value: level.multiplier } }}</li>
            @for (reward of rewardsForLevel(level.id); track reward._id) {
              <li><mat-icon>redeem</mat-icon>{{reward.name}}</li>
            }
            @if (level.id === 'platinum') {
              <li><mat-icon>verified</mat-icon>{{ 'club.platinumPerk' | t }}</li>
            }
          </ul>
        </div>
      }
    </div>
  </section>

  <div class="grid-2">
    <!-- Historique -->
    <section class="card">
      <h2 class="card-title">{{ 'club.history' | t }}</h2>
      @if (history.length === 0) {
        <p class="muted">{{ 'club.historyEmpty' | t }}</p>
      } @else {
        <ul class="history">
          @for (transaction of visibleHistory; track transaction._id) {
            <li>
              <div class="history-text">
                <span>{{ 'club.reasons.' + transaction.reason | t }}</span>
                @if (transaction.note) { <small class="muted ellipsis">{{transaction.note}}</small> }
                <small class="muted">
                  {{ date(transaction.createdAt) }}
                  @if (isLocked(transaction)) {
                    · <mat-icon class="tiny">lock_clock</mat-icon> {{ 'club.lockedUntil' | t: { date: date(transaction.availableAt) } }}
                  }
                </small>
              </div>
              <strong [class.positive]="transaction.points > 0" [class.negative]="transaction.points < 0">
                {{ transaction.points > 0 ? '+' : '' }}{{transaction.points}}
              </strong>
            </li>
          }
        </ul>
        @if (history.length > 10) {
          <button type="button" class="btn-text" (click)="showAllHistory = !showAllHistory">
            {{ (showAllHistory ? 'club.showLess' : 'club.showMore') | t }}
          </button>
        }
      }
    </section>

    <!-- Classement -->
    <section class="card">
      <h2 class="card-title">{{ 'club.leaderboard' | t }}</h2>
      @if (leaderboard.length === 0) {
        <p class="muted">{{ 'club.leaderboardEmpty' | t }}</p>
      } @else {
        <ol class="leaderboard">
          @for (entry of leaderboard; track entry.userId; let i = $index) {
            <li [class.me]="entry.userId === myUserId">
              <span class="rank" [class]="'rank rank-' + (i + 1)">{{i + 1}}</span>
              <span class="ellipsis">{{ entry.userId === myUserId ? ('club.you' | t) : entry.pseudo }}</span>
              <strong>{{entry.points | number}}</strong>
            </li>
          }
        </ol>
      }
    </section>
  </div>

  <p class="muted rules"><mat-icon class="tiny">info</mat-icon>{{ 'club.rules' | t }}</p>
}
